'use client';

import React, { useState, useEffect } from 'react';
import Script from 'next/script';

// ─── Types ───────────────────────────────────────────────────────────────────

interface NavLink {
  label: string;
  href: string;
}

interface StatItem {
  value: string;
  label: string;
  source: string;
}

interface PainPoint {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Service {
  icon: React.ReactNode;
  title: string;
  bullets: string[];
  highlight?: string;
}

interface Industry {
  icon: React.ReactNode;
  name: string;
  regulatory: string;
  risk: string;
  outcome: string;
}

interface Differentiator {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
}

// ─── Icon Components ─────────────────────────────────────────────────────────

const ShieldIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 10c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.249-8.25-3.286z" />
  </svg>;


const AlertIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>;


const CurrencyIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>;


const LockIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>;


const MapIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
  </svg>;


const ChartIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>;


const ClipboardIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
  </svg>;


const UserIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>;


const BuildingIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
  </svg>;


const HeartIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>;


const BriefcaseIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
  </svg>;


const CogIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>;


const CheckIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>;


const ArrowRightIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>;


const PhoneIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>;


const StarIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>;


const QuoteIcon = ({ className }: {className?: string;}) =>
<svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
  </svg>;


// ─── Data ─────────────────────────────────────────────────────────────────────

const navLinks: NavLink[] = [
{ label: 'Services', href: '#services' },
{ label: 'Who It\'s For', href: '#industries' },
{ label: 'Why Digital Enact', href: '#why-us' },
{ label: 'Book a Consultation', href: '#book' }];


const stats: StatItem[] = [
{ value: '$4M+', label: 'Annual cost of an in-house 24/7 SOC', source: 'Industry benchmark' },
{ value: '58%', label: 'Reduction in breach costs with an IR plan', source: 'Ponemon Institute, IBM' },
{ value: '3.4M', label: 'Global cybersecurity job shortage', source: 'ISC², 2024' },
{ value: '70%', label: 'Reduction in compliance prep time with AI-enabled GRC tools', source: 'Industry research' }];


const painPoints: PainPoint[] = [
{
  icon: <ClipboardIcon className="w-8 h-8" />,
  title: 'Regulatory pressure is increasing',
  description:
  'Privacy Act 1988 reforms (2024), the NDB scheme, SOCI Act, and the ACSC Essential Eight maturity model are raising the compliance bar. Non-compliance carries serious financial penalties and lasting reputational damage.'
},
{
  icon: <AlertIcon className="w-8 h-8" />,
  title: 'Breaches are costly and common',
  description:
  'Every hour of undetected threat activity has a real business cost. Ransomware can halt operations for days. The ACSC reports cybercrime as an escalating national threat — and Australian businesses are firmly in the crosshairs.'
},
{
  icon: <CurrencyIcon className="w-8 h-8" />,
  title: 'In-house security is unaffordable for most',
  description:
  'Building a 24/7 SOC requires $1.5M+ in infrastructure, $2M–$3M annually in staffing, and $500K+ in licensing. Most Australian mid-market businesses simply cannot justify that investment — nor should they have to.'
},
{
  icon: <LockIcon className="w-8 h-8" />,
  title: 'Cyber insurance won\'t protect you if controls are missing',
  description:
  'Insurers mandate MFA, EDR, incident response plans, and encryption. Without these controls, carriers can legally deny your breach claim — or triple your premium at renewal. Readiness is not optional.'
}];


const services: Service[] = [
{
  icon: <MapIcon className="w-7 h-7" />,
  title: 'Cyber Strategy & Roadmap',
  bullets: [
  'Tailored cybersecurity strategy aligned to your business goals and Australian regulatory obligations',
  'ACSC Essential Eight maturity assessment and uplift planning',
  'Long-term risk reduction roadmap — not a one-off audit']

},
{
  icon: <ChartIcon className="w-7 h-7" />,
  title: 'Risk Assessment & Gap Analysis',
  bullets: [
  'Identify security posture gaps across people, process, and technology',
  'Benchmark against ASD guidelines and Essential Eight maturity levels',
  'Practical, prioritised remediation recommendations']

},
{
  icon: <ShieldIcon className="w-7 h-7" />,
  title: 'MSSP Selection & Guidance',
  bullets: [
  'Vendor-agnostic evaluation of Managed Security Service Providers',
  '24/7 monitoring, threat detection, and response at a fraction of in-house cost',
  'Predictable $250K–$400K/yr vs $4M+ in-house SOC costs'],

  highlight: 'Save up to 90% vs in-house SOC'
},
{
  icon: <ClipboardIcon className="w-7 h-7" />,
  title: 'Compliance Readiness',
  bullets: [
  'Navigate Privacy Act 1988 (2024 reforms), NDB scheme, SOCI Act, and Essential Eight',
  'Policy development, evidence collection, and audit preparation',
  'AI-enabled GRC tools can reduce compliance prep time by up to 70%']

},
{
  icon: <AlertIcon className="w-7 h-7" />,
  title: 'Incident Response Planning',
  bullets: [
  'Develop and test documented incident response plans — required for cyber insurance eligibility',
  'Ensure your team knows exactly what to do when (not if) a breach occurs',
  'An IR plan can cut breach-related costs by up to 58% (Ponemon Institute, IBM)'],

  highlight: 'Cuts breach costs by up to 58%'
},
{
  icon: <LockIcon className="w-7 h-7" />,
  title: 'Cyber Insurance Readiness',
  bullets: [
  'Assess whether your security controls meet insurer requirements',
  'Reduce premiums and avoid claim denial: MFA, EDR, IR plan, encryption, PAM',
  'Insurability readiness reporting and gap remediation guidance']

}];


const industries: Industry[] = [
{
  icon: <HeartIcon className="w-6 h-6" />,
  name: 'Healthcare',
  regulatory: 'Privacy Act 1988, NDB scheme, My Health Records Act, ACSC Essential Eight',
  risk: 'Ransomware targeting patient data, telehealth security, insider threats to electronic health records',
  outcome: 'Protecting sensitive patient information while enabling digital health innovation'
},
{
  icon: <CurrencyIcon className="w-6 h-6" />,
  name: 'Finance & Fintech',
  regulatory: 'Privacy Act 1988 (2024 reforms), APRA CPS 234, NDB scheme, ASD guidelines, SOCI Act',
  risk: 'Insider threats, SOC gaps, real-time payment fraud, legacy infrastructure vulnerabilities',
  outcome: 'Ensuring APRA CPS 234 compliance while protecting customer financial data'
},
{
  icon: <BriefcaseIcon className="w-6 h-6" />,
  name: 'Professional Services',
  regulatory: 'Privacy Act 1988, NDB scheme, legal professional privilege, accounting data obligations',
  risk: 'Phishing, business email compromise, client data breaches, supply chain attacks',
  outcome: 'Protecting client confidentiality and firm reputation in a distributed work environment'
},
{
  icon: <BuildingIcon className="w-6 h-6" />,
  name: 'Government & Local Councils',
  regulatory: 'SOCI Act, Australian Government ISM, Essential Eight, ACSC guidelines, state privacy legislation',
  risk: 'Ransomware on council systems, citizen data breaches, legacy infrastructure, inter-agency data sharing',
  outcome: 'Aligning council cybersecurity with Essential Eight and SOCI obligations on limited budgets'
},
{
  icon: <CogIcon className="w-6 h-6" />,
  name: 'Retail',
  regulatory: 'Privacy Act 1988, NDB scheme, PCI-DSS for payment processing',
  risk: 'Payment data theft, supply chain attacks, distributed workforce, seasonal staff BYOD',
  outcome: 'Securing payment systems and customer data across physical and digital channels'
},
{
  icon: <CogIcon className="w-6 h-6" />,
  name: 'Manufacturing, Water Utilities, Electricity Utilities',
  regulatory: 'SOCI Act (critical infrastructure), NDB scheme, ACSC Essential Eight, supply chain security',
  risk: 'OT attacks, IoT vulnerabilities, ransomware halting production, IP theft',
  outcome: 'Protecting operational technology while securing the digital supply chain'
}];


const differentiators: Differentiator[] = [
{
  icon: <UserIcon className="w-8 h-8" />,
  title: 'One Advisor. Full Accountability.',
  description:
  'You work directly with Kamal Salwan — not a rotating team of junior consultants. Single point of contact from assessment through to implementation guidance. The same person who understands your business strategy also understands your security needs.'
},
{
  icon: <ChartIcon className="w-8 h-8" />,
  title: 'Business-Aligned, Not Tech-First',
  description:
  'Cyber outcomes are always tied to business goals: protecting revenue, reducing liability, meeting regulatory obligations. No unnecessary complexity or technology for technology\'s sake. Security is a business enabler, not just an IT function.'
},
{
  icon: <MapIcon className="w-8 h-8" />,
  title: 'Vendor-Agnostic, Melbourne-Based',
  description:
  'No vendor commissions or supplier incentives influencing recommendations. Genuinely independent advice on MSSPs, tools, and security architectures. Local to Melbourne — available for face-to-face engagement with Australian businesses.'
},
{
  icon: <ClipboardIcon className="w-8 h-8" />,
  title: 'Australian Regulatory Expertise',
  description:
  'Deep understanding of the Australian regulatory landscape: Privacy Act, NDB scheme, SOCI Act, Essential Eight, ASD guidelines, and the Cyber Security Strategy 2023–2030. Advice calibrated to your business size, industry, and risk context.'
}];


const testimonials: Testimonial[] = [
{
  quote:
  'Kamal helped us understand exactly where our security gaps were and gave us a clear, practical roadmap to address them. For the first time, our board had confidence in our cyber posture.',
  name: '[Client Name]',
  title: 'Chief Information Officer',
  company: '[Organisation Name] — [Industry]'
},
{
  quote:
  'We were facing a cyber insurance renewal with a 60% premium increase. Kamal identified the missing controls, helped us implement them, and we renewed at a significantly lower rate.',
  name: '[Client Name]',
  title: 'CFO',
  company: '[Organisation Name] — [Industry]'
},
{
  quote:
  'The compliance advisory work Digital Enact provided saved us months of preparation. Kamal translated complex regulatory requirements into actions our team could actually execute.',
  name: '[Client Name]',
  title: 'Head of IT',
  company: '[Organisation Name] — [Industry]'
}];


// ─── Main Component ───────────────────────────────────────────────────────────

export default function CyberSecurityPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hubspotLoaded, setHubspotLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="font-ubuntu text-brand-body bg-white">
      {/* ── Top Bar ── */}
      <div className="bg-brand-topbar border-b border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="https://twitter.com/DE_Kamals" target="_blank" rel="noopener noreferrer" className="text-brand-body hover:text-primary transition-colors text-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" /></svg>
            </a>
            <a href="https://www.linkedin.com/company/digital-enact-pty-ltd./" target="_blank" rel="noopener noreferrer" className="text-brand-body hover:text-primary transition-colors text-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
          </div>
          <div className="flex items-center gap-2 text-sm text-brand-body">
            <PhoneIcon className="w-4 h-4 text-accent" />
            <span className="font-medium">Call Today!</span>
            <a href="tel:+61391101561" className="text-primary font-semibold hover:text-accent transition-colors">03 9110 1561</a>
          </div>
        </div>
      </div>

      {/* ── Sticky Header ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-white border-b border-gray-100'}`
        }>
        
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <a href="https://digitalenact.com.au/" className="flex-shrink-0">
            <img
              src="https://digitalenact.com.au/wp-content/uploads/2020/01/digital-enact-logo-5.webp"
              alt="Digital Enact — Co-creating Digital Strategy"
              className="h-12 w-auto" />
            
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
            link.label === 'Book a Consultation' ?
            <button
              key={link.label}
              onClick={() => scrollToSection(link.href)}
              className="ml-4 bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary-dark transition-colors duration-200">
              
                  {link.label}
                </button> :

            <button
              key={link.label}
              onClick={() => scrollToSection(link.href)}
              className="px-4 py-2 text-sm font-medium text-brand-dark hover:text-primary transition-colors duration-200">
              
                  {link.label}
                </button>

            )}
          </nav>

          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 text-brand-dark"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu">
            
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ?
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> :

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
            {navLinks.map((link) =>
          <button
            key={link.label}
            onClick={() => scrollToSection(link.href)}
            className={`text-left py-2 text-sm font-medium ${
            link.label === 'Book a Consultation' ? 'bg-primary text-white px-4 py-2.5 mt-2' : 'text-brand-dark hover:text-primary'} transition-colors`
            }>
            
                {link.label}
              </button>
          )}
          </div>
        }
      </header>

      {/* ── HERO SECTION ── */}
      <section
        id="hero"
        className="relative min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden"
        style={{
          backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1224a2152-1780141830948.png",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/55" />

        {/* Orange panel — Digital Enact signature style */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
            <div
              className="max-w-2xl p-8 md:p-12"
              style={{ backgroundColor: 'rgba(217, 134, 47, 0.88)' }}>
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold uppercase tracking-widest px-3 py-1.5 mb-6">
                <ShieldIcon className="w-3.5 h-3.5" />
                Cyber Security Advisory — Melbourne, Australia
              </div>

              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                Your Business Is a Target.
                <br />
                <span className="text-white">Let&apos;s Make Sure</span>
                <br />
                It&apos;s Also Resilient.
              </h1>

              <div className="w-16 h-1 bg-white mb-6" />

              <p className="text-white/95 text-base md:text-lg leading-relaxed mb-8">
                Melbourne&apos;s independent cybersecurity advisor — helping Australian businesses
                strengthen their security posture, meet compliance obligations, and build lasting cyber resilience.
                Vendor-agnostic. Business-aligned. Locally based.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => scrollToSection('#book')}
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-7 py-3.5 text-sm font-bold hover:bg-primary-dark transition-colors duration-200">
                  
                  Book a Free Consultation
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollToSection('#services')}
                  className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white text-white px-7 py-3.5 text-sm font-semibold hover:bg-white/30 transition-colors duration-200">
                  
                  View Services
                </button>
              </div>

              <p className="mt-5 text-white/80 text-xs">
                Melbourne-based · Serving Australian businesses across healthcare, finance, professional services, government, retail &amp; manufacturing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-primary py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) =>
            <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-black text-white mb-1">{stat.value}</div>
                <div className="text-white/80 text-xs md:text-sm leading-snug">{stat.label}</div>
                <div className="text-white/50 text-xs mt-1">{stat.source}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS / WHY IT MATTERS ── */}
      <section id="why-matters" className="py-20" style={{ backgroundColor: '#f3f3f3' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              The Cyber Threat Landscape in Australia Has Never Been More Urgent
            </h2>
            <p className="text-brand-body text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              Australian businesses face a perfect storm of escalating cyber threats, tightening regulation, and a
              global shortage of skilled security professionals. The question is no longer <em>if</em> you will be
              targeted — it&apos;s whether you&apos;ll be ready when it happens.
            </p>
          </div>

          {/* Regulatory context */}
          <div className="bg-white border-l-4 border-accent p-6 md:p-8 mb-12 shadow-sm">
            <h3 className="text-lg font-bold text-brand-dark mb-4">Australian Regulatory Context</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
              { reg: 'ACSC Essential Eight', desc: 'The benchmark maturity model for Australian organisations — Levels 0–3 across eight mitigation strategies' },
              { reg: 'Privacy Act 1988 (2024 reforms)', desc: 'Penalties up to $50M or 30% of adjusted turnover; expanded personal information definition and stronger individual rights' },
              { reg: 'Notifiable Data Breaches (NDB) scheme', desc: 'Mandatory notification to OAIC and affected individuals within 30 days of becoming aware of an eligible breach' },
              { reg: 'Security of Critical Infrastructure (SOCI) Act', desc: 'Positive security obligations across 11 sectors; mandatory incident reporting within 12–72 hours' },
              { reg: 'ASD Guidelines & ISM', desc: 'Australian Signals Directorate guidelines underpinning government and enterprise cybersecurity best practice' },
              { reg: 'Cyber Security Strategy 2023–2030', desc: 'Australia\'s national ambition to become the most cyber-secure nation in the world by 2030' }].
              map((item, i) =>
              <div key={i} className="flex gap-3">
                  <CheckIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-brand-dark text-sm">{item.reg}: </span>
                    <span className="text-brand-body text-sm">{item.desc}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pain point cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {painPoints.map((point, i) =>
            <div
              key={i}
              className="bg-white p-7 shadow-sm hover:shadow-md transition-shadow duration-300 group">
              
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                    {point.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-brand-dark mb-2">{point.title}</h3>
                    <p className="text-brand-body text-sm leading-relaxed">{point.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── SERVICES OVERVIEW ── */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              How Digital Enact Helps You Build a Stronger Security Posture
            </h2>
            <p className="text-brand-body text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              Kamal Salwan works with your business to translate cybersecurity complexity into clear, actionable
              strategy — without trying to sell you unnecessary technology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) =>
            <div
              key={i}
              className="border border-gray-200 p-7 hover:border-accent hover:shadow-md transition-all duration-300 group relative">
              
                {service.highlight &&
              <div className="absolute top-4 right-4 bg-teal text-white text-xs font-bold px-2.5 py-1">
                    {service.highlight}
                  </div>
              }
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300 mb-5">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-4">{service.title}</h3>
                <ul className="space-y-2.5">
                  {service.bullets.map((bullet, j) =>
                <li key={j} className="flex gap-2.5 text-sm text-brand-body leading-relaxed">
                      <CheckIcon className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>

          {/* MSSP vs In-house comparison */}
          <div className="mt-14 bg-brand-dark text-white p-8 md:p-10">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">MSSP vs In-House SOC — The Numbers</h3>
            <p className="text-white/70 text-sm mb-8">For most Australian mid-market businesses, a Managed Security Service Provider delivers superior coverage at a fraction of the cost.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 pr-6 text-white/60 font-medium">Factor</th>
                    <th className="text-left py-3 pr-6 text-white/60 font-medium">In-House SOC</th>
                    <th className="text-left py-3 text-teal font-semibold">MSSP (via Digital Enact)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {[
                  ['Infrastructure & tools', '$1.5M+ upfront', 'Included in subscription'],
                  ['Annual staffing', '$2M–$3M/yr (6 analysts, 2 engineers, 1 CISO)', 'Included in subscription'],
                  ['Licensing & training', '$500K+/yr', 'Included in subscription'],
                  ['Total approximate cost', '$4M+ per year', '$250K–$400K/yr'],
                  ['Coverage', 'Business hours + on-call gaps', '24/7/365'],
                  ['Compliance reporting', 'Manual/internal', 'Built-in, audit-ready'],
                  ['Scalability', 'Requires additional headcount', 'Scales with subscription']].
                  map(([factor, inhouse, mssp], i) =>
                  <tr key={i}>
                      <td className="py-3 pr-6 text-white/80 font-medium">{factor}</td>
                      <td className="py-3 pr-6 text-white/60">{inhouse}</td>
                      <td className="py-3 text-teal font-medium">{mssp}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR — INDUSTRIES ── */}
      <section id="industries" className="py-20" style={{ backgroundColor: '#e5e5e5' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Serving Australian Businesses Across Key Industries
            </h2>
            <p className="text-brand-body text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              Cyber risk is not one-size-fits-all. Different industries face different threats, different regulatory
              obligations, and different consequences of a breach.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, i) =>
            <div
              key={i}
              className="bg-white p-7 hover:shadow-md transition-shadow duration-300 group">
              
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-accent/15 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                    {industry.icon}
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark">{industry.name}</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">Regulatory Focus</span>
                    <p className="text-brand-body text-sm mt-1 leading-relaxed">{industry.regulatory}</p>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary">Key Risks</span>
                    <p className="text-brand-body text-sm mt-1 leading-relaxed">{industry.risk}</p>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-sm font-medium text-brand-dark italic">&ldquo;{industry.outcome}&rdquo;</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── WHY DIGITAL ENACT ── */}
      <section id="why-us" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Why Work With Digital Enact?
            </h2>
            <p className="text-brand-body text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              When it comes to cybersecurity, you don&apos;t just need solutions — you need a trusted advisor who
              understands your business, your obligations, and your risk appetite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((diff, i) =>
            <div
              key={i}
              className="flex gap-6 p-7 border border-gray-200 hover:border-primary hover:shadow-md transition-all duration-300 group">
              
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  {diff.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-brand-dark mb-3">{diff.title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{diff.description}</p>
                </div>
              </div>
            )}
          </div>

          {/* Kamal Salwan profile */}
          <div className="mt-14 bg-primary text-white p-8 md:p-10 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0 w-24 h-24 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-4 border-white/30">
              <img
                src="/assets/images/_N6A5429_low_res-1785208752358.jpg"
                alt="Kamal Salwan – Founder & Principal Advisor, Digital Enact"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1">Kamal Salwan</h3>
              <p className="text-white/70 text-sm mb-3">Founder & Principal Advisor, Digital Enact · Melbourne, Australia</p>
              <p className="text-white/90 text-sm leading-relaxed">
                Kamal is a digital transformation and cybersecurity strategist with deep experience helping Australian
                organisations navigate complex technology and regulatory landscapes. As a sole advisor, he provides the
                kind of direct, accountable, and genuinely independent advice that larger consultancies simply cannot
                offer. Every engagement is led personally by Kamal — from initial assessment through to implementation
                guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF / TESTIMONIALS ── */}
      <section id="testimonials" className="py-20" style={{ backgroundColor: '#f3f3f3' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Trusted by Australian Businesses
            </h2>
            <p className="text-brand-body text-sm">
              <em>Replace placeholder content below with real testimonials and client logos.</em>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {testimonials.map((t, i) =>
            <div key={i} className="bg-white p-7 shadow-sm relative">
                <QuoteIcon className="w-8 h-8 text-accent/30 mb-4" />
                <div className="flex mb-3">
                  {[...Array(5)].map((_, j) =>
                <StarIcon key={j} className="w-4 h-4 text-accent" />
                )}
                </div>
                <p className="text-brand-body text-sm leading-relaxed mb-6 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="border-t border-gray-100 pt-4">
                  <div className="font-bold text-brand-dark text-sm">{t.name}</div>
                  <div className="text-xs text-brand-body">{t.title}</div>
                  <div className="text-xs text-accent font-medium mt-0.5">{t.company}</div>
                </div>
              </div>
            )}
          </div>

          {/* Logo placeholders */}
          <div>
            <p className="text-center text-xs text-brand-body uppercase tracking-widest font-semibold mb-6">Clients &amp; Partners</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[...Array(6)].map((_, i) =>
              <div
                key={i}
                className="w-36 h-16 bg-white border border-gray-200 flex items-center justify-center text-xs text-gray-400 font-medium">
                
                  [Logo {i + 1}]
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA / BOOK CONSULTATION ── */}
      <section id="book" className="py-20" style={{ backgroundColor: '#4c4943' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Strengthen Your Cyber Resilience?
            </h2>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-3">
              Book a free, no-obligation consultation with Kamal Salwan. In 30 minutes, you&apos;ll have a clearer
              picture of your current cyber risk and where to focus next.
            </p>
            <p className="text-white/60 text-sm">
              No vendor agenda. No sales pressure. Just expert, independent advice tailored to your Australian business.
            </p>
          </div>

          {/* HubSpot Meeting Scheduler */}
          <div className="bg-white p-4 md:p-6">
            {/* Start of Meetings Embed Script */}
            <div
              className="meetings-iframe-container"
              data-src="https://info.digitalenact.com.au/meetings/kamal-salwan/cyber-security-consultation?embed=true" />
            
            {/* End of Meetings Embed Script */}
          </div>

          {/* Fallback CTA */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm mb-4">Prefer to get in touch directly?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:+61391101561"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-6 py-3 text-sm font-semibold hover:bg-accent-dark transition-colors">
                
                <PhoneIcon className="w-4 h-4" />
                Call 03 9110 1561
              </a>
              <a
                href="mailto:kamal.salwan@digitalenact.com.au"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors">
                
                kamal.salwan@digitalenact.com.au
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <a href="https://digitalenact.com.au/">
                <img
                  src="https://digitalenact.com.au/wp-content/uploads/2020/01/digital-enact-logo-5.webp"
                  alt="Digital Enact"
                  className="h-10 w-auto mb-4" />
                
              </a>
              <p className="text-brand-body text-sm leading-relaxed max-w-xs">
                Independent cybersecurity advice for Australian businesses. Vendor-agnostic. Business-aligned.
                Melbourne-based.
              </p>
              <div className="flex gap-3 mt-5">
                <a href="https://twitter.com/DE_Kamals" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-gray-100 flex items-center justify-center text-brand-body hover:bg-primary hover:text-white transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" /></svg>
                </a>
                <a href="https://www.linkedin.com/company/digital-enact-pty-ltd./" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-gray-100 flex items-center justify-center text-brand-body hover:bg-primary hover:text-white transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                </a>
              </div>
            </div>

            {/* Find Us */}
            <div>
              <h4 className="text-accent font-bold text-sm uppercase tracking-wider mb-4">Find Us</h4>
              <p className="text-brand-body text-sm leading-relaxed">
                6 Driftwood Drive<br />
                Glen Waverley, VIC 3150<br />
                Melbourne, Australia
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-accent font-bold text-sm uppercase tracking-wider mb-4">Contact Us</h4>
              <div className="space-y-2">
                <a href="tel:+61391101561" className="flex items-center gap-2 text-brand-body text-sm hover:text-primary transition-colors">
                  <PhoneIcon className="w-4 h-4 text-accent" />
                  03 9110 1561
                </a>
                <a href="mailto:kamal.salwan@digitalenact.com.au" className="text-brand-body text-sm hover:text-primary transition-colors break-all">
                  kamal.salwan@digitalenact.com.au
                </a>
                <a href="https://digitalenact.com.au/" className="text-brand-body text-sm hover:text-primary transition-colors">
                  digitalenact.com.au
                </a>
              </div>
            </div>
          </div>

          {/* Copyright bar */}
          <div
            className="-mx-6 px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-3 text-white text-xs"
            style={{ backgroundColor: '#b46a21' }}>
            
            <span>Copyright &copy;2016–2026 Digital Enact. All rights reserved.</span>
            <div className="flex gap-4">
              <a href="https://digitalenact.com.au/privacy/" className="hover:underline">Privacy Policy</a>
              <a href="https://digitalenact.com.au/" className="hover:underline">digitalenact.com.au</a>
            </div>
          </div>
        </div>
      </footer>

      {/* HubSpot Meetings Embed Script */}
      <Script
        src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"
        strategy="lazyOnload"
        onLoad={() => setHubspotLoaded(true)} />
      
    </div>);

}